import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Tooltip,
  Button,
  Autocomplete,
  TextField,
  Alert,
  Collapse,
  IconButton,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Download as DownloadIcon,
  School as SchoolIcon,
  Public as PublicIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { adminService } from '../../services/adminService';
import Papa from 'papaparse';
import { detectSchoolType, getSchoolTypeInfo, SCHOOL_TYPE_LABELS } from '../../utils/schoolTypeDetection';

const AcademicData = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [schools, setSchools] = useState([]);
  const [error, setError] = useState(null);
  
  // Filter states
  const [stateFilter, setStateFilter] = useState([]);
  const [teamFilter, setTeamFilter] = useState([]);
  const [dataStatusFilter, setDataStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState([]);
  
  // Sorting
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');
  
  // Expanded rows
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Load data
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.getAcademicDataCoverage(enqueueSnackbar);
      setMetrics(response.metrics);
      setSchools(response.schools);
    } catch (err) {
      console.error('Error loading academic data:', err);
      setError('Failed to load academic data coverage');
      enqueueSnackbar('Error loading academic data coverage', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Get unique states and teams for filters
  const uniqueStates = useMemo(() => {
    const states = [...new Set(schools.map(s => s.state).filter(Boolean))];
    return states.sort();
  }, [schools]);

  const uniqueTeams = useMemo(() => {
    const teamsSet = new Set();
    schools.forEach(school => {
      if (school.teams) {
        school.teams.split(', ').forEach(team => teamsSet.add(team.trim()));
      }
    });
    return [...teamsSet].sort();
  }, [schools]);

  // Filter schools
  const filteredSchools = useMemo(() => {
    return schools.filter(school => {
      // State filter (multi-select)
      if (stateFilter.length > 0 && !stateFilter.includes(school.state)) {
        return false;
      }
      
      // Team filter
      if (teamFilter.length > 0) {
        const schoolTeams = school.teams ? school.teams.split(', ').map(t => t.trim()) : [];
        if (!teamFilter.some(t => schoolTeams.includes(t))) {
          return false;
        }
      }
      
      // Data status filter
      if (dataStatusFilter === 'with-data' && !school.has_data) {
        return false;
      }
      if (dataStatusFilter === 'without-data' && school.has_data) {
        return false;
      }
      
      // Type filter (multi-select)
      if (typeFilter.length > 0) {
        const schoolType = detectSchoolType(school);
        if (!typeFilter.includes(schoolType)) {
          return false;
        }
      }
      
      return true;
    });
  }, [schools, stateFilter, teamFilter, dataStatusFilter, typeFilter]);

  // Calculate dynamic metrics based on filtered schools
  const filteredMetrics = useMemo(() => {
    const round = (num, decimals) => {
      return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
    };
    
    const total = filteredSchools.length;
    const withData = filteredSchools.filter(s => s.has_data).length;
    const uniqueStates = new Set(filteredSchools.map(s => s.state).filter(Boolean));
    const statesWithDataSet = new Set(
      filteredSchools.filter(s => s.has_data && s.state).map(s => s.state)
    );
    
    return {
      total_schools: total,
      schools_with_data: withData,
      schools_with_data_pct: total > 0 ? round(withData / total * 100, 1) : 0,
      total_states: uniqueStates.size,
      states_with_data: statesWithDataSet.size,
      states_with_data_pct: uniqueStates.size > 0 ? round(statesWithDataSet.size / uniqueStates.size * 100, 1) : 0,
      states_list: Array.from(statesWithDataSet).sort()
    };
  }, [filteredSchools]);

  // Sort schools
  const sortedSchools = useMemo(() => {
    const sorted = [...filteredSchools];
    sorted.sort((a, b) => {
      let aVal, bVal;
      
      switch (orderBy) {
        case 'name':
          aVal = a.name || '';
          bVal = b.name || '';
          break;
        case 'state':
          aVal = a.state || '';
          bVal = b.state || '';
          break;
        case 'teams':
          aVal = a.teams || '';
          bVal = b.teams || '';
          break;
        case 'has_data':
          aVal = a.has_data ? 1 : 0;
          bVal = b.has_data ? 1 : 0;
          break;
        case 'metric_types':
          aVal = a.metric_types || '';
          bVal = b.metric_types || '';
          break;
        case 'metric_categories':
          aVal = a.metric_categories || '';
          bVal = b.metric_categories || '';
          break;
        case 'type':
          aVal = getSchoolTypeInfo(detectSchoolType(a)).label;
          bVal = getSchoolTypeInfo(detectSchoolType(b)).label;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [filteredSchools, orderBy, order]);

  // Get unique teams for a school
  const getUniqueTeams = (teamsString) => {
    if (!teamsString) return [];
    const teamsArray = teamsString.split(', ').map(t => t.trim());
    return [...new Set(teamsArray)];
  };

  // Helper to format metric display value
  const formatMetricValue = (metric) => {
    const hasValidValue = metric.value && 
                          metric.value.toLowerCase() !== 'nan' && 
                          metric.value.trim() !== '';
    const hasNumericValue = metric.numeric_value !== null && 
                            metric.numeric_value !== undefined &&
                            !isNaN(metric.numeric_value);

    // Check if this is a performance level (score) vs proficiency rate (percentage)
    const isPerformanceLevel = metric.type && metric.type.includes('performance_level');
    
    if (hasValidValue) {
      // If we have a valid string value, use it
      let secondary = null;
      if (hasNumericValue) {
        // Performance levels are already scores (0-100), don't multiply by 100
        // Proficiency rates are decimals (0-1), need to multiply by 100
        secondary = isPerformanceLevel 
          ? metric.numeric_value.toFixed(1)
          : `${(metric.numeric_value * 100).toFixed(1)}%`;
      }
      return {
        primary: metric.value,
        secondary: secondary
      };
    } else if (hasNumericValue) {
      // If we only have numeric value
      const formatted = isPerformanceLevel
        ? metric.numeric_value.toFixed(1) // Show as-is for performance levels
        : `${(metric.numeric_value * 100).toFixed(1)}%`; // Convert to % for proficiency rates
      return {
        primary: formatted,
        secondary: null
      };
    } else {
      // No data
      return {
        primary: '-',
        secondary: null
      };
    }
  };

  // Format metric type for display
  const formatMetricType = (metricType) => {
    if (!metricType) return '-';
    return metricType.split(', ').map(type => {
      // Convert snake_case to Title Case
      return type.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    }).join(', ');
  };

  // Format metric category for display
  const formatMetricCategory = (metricCategory) => {
    if (!metricCategory) return '-';
    return metricCategory.split(', ').map(category => {
      const categoryMap = {
        'ela': 'ELA',
        'math': 'Math',
        'science': 'Science',
        'overall': 'Overall',
      };
      return categoryMap[category.toLowerCase()] || category.charAt(0).toUpperCase() + category.slice(1);
    }).join(', ');
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  const handleToggleRow = (uuid) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(uuid)) {
        newSet.delete(uuid);
      } else {
        newSet.add(uuid);
      }
      return newSet;
    });
  };

  const handleExportCSV = () => {
    const exportData = sortedSchools.map(school => {
      const schoolType = detectSchoolType(school);
      const typeInfo = getSchoolTypeInfo(schoolType);
      
      const baseData = {
        'School Name': school.name,
        'State': school.state,
        'Type': typeInfo.label,
        'Has Data': school.has_data ? 'Yes' : 'No',
        'Metric Types': formatMetricType(school.metric_types),
        'Metric Categories': formatMetricCategory(school.metric_categories),
        'Teams': getUniqueTeams(school.teams).join(', '),
      };
      
      // Add detailed scores if available
      if (school.metrics && school.metrics.length > 0) {
        const scores = school.metrics.map(m => {
          const formatted = formatMetricValue(m);
          let scoreStr = `${formatMetricCategory(m.category)}: ${formatted.primary}`;
          if (m.notes) {
            scoreStr += ` - ${m.notes}`;
          }
          if (formatted.secondary) {
            scoreStr += ` (${formatted.secondary})`;
          }
          return scoreStr;
        }).join('; ');
        baseData['Scores'] = scores;
      }
      
      return baseData;
    });

    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const date = new Date().toISOString().split('T')[0];
    a.download = `academic-data-coverage-${date}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    enqueueSnackbar('Data exported successfully', { variant: 'success' });
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '60vh',
          gap: 3
        }}>
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" color="text.secondary">
            Loading Academic Data...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      {/* Header */}
      <Paper elevation={0} sx={{ mb: 3, p: 3, bgcolor: 'background.default' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Academic Data Coverage
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View academic data coverage for EDC schools across all teams.
        </Typography>
      </Paper>

      {/* Metrics Cards - Dynamic based on filters */}
      {metrics && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2.5, bgcolor: 'background.paper' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SchoolIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                    {filteredMetrics.schools_with_data}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    of {filteredMetrics.total_schools} Schools
                  </Typography>
                </Box>
                <Chip 
                  label={`${filteredMetrics.schools_with_data_pct}%`}
                  color={filteredMetrics.schools_with_data_pct >= 70 ? 'success' : filteredMetrics.schools_with_data_pct >= 40 ? 'warning' : 'error'}
                  sx={{ fontSize: '1.1rem', fontWeight: 'bold', height: 36, px: 1 }}
                />
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Tooltip
              title={
                filteredMetrics.states_list.length > 0 ? (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      States with Data:
                    </Typography>
                    {filteredMetrics.states_list.map(state => (
                      <Typography key={state} variant="body2">
                        {state}
                      </Typography>
                    ))}
                  </Box>
                ) : 'No states with data in current filter'
              }
              arrow
              placement="top"
            >
              <Paper sx={{ p: 2.5, bgcolor: 'background.paper', cursor: 'help' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PublicIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                      {filteredMetrics.states_with_data}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      of {filteredMetrics.total_states} States
                    </Typography>
                  </Box>
                  <Chip 
                    label={`${filteredMetrics.states_with_data_pct}%`}
                    color={filteredMetrics.states_with_data_pct >= 70 ? 'success' : filteredMetrics.states_with_data_pct >= 40 ? 'warning' : 'error'}
                    sx={{ fontSize: '1.1rem', fontWeight: 'bold', height: 36, px: 1 }}
                  />
                </Box>
              </Paper>
            </Tooltip>
          </Grid>
        </Grid>
      )}

      {/* Filters and Export */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2.4}>
            <Autocomplete
              multiple
              size="small"
              options={uniqueStates}
              value={stateFilter}
              onChange={(e, newValue) => setStateFilter(newValue)}
              disableCloseOnSelect
              renderInput={(params) => (
                <TextField {...params} label="States" placeholder="Select states" />
              )}
              renderOption={(props, option, { selected }) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps}>
                    <Checkbox
                      checked={selected}
                      sx={{ mr: 1 }}
                    />
                    {option}
                  </li>
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={option}
                      size="small"
                      {...tagProps}
                    />
                  );
                })
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Autocomplete
              multiple
              size="small"
              options={Object.keys(SCHOOL_TYPE_LABELS)}
              value={typeFilter}
              onChange={(e, newValue) => setTypeFilter(newValue)}
              disableCloseOnSelect
              getOptionLabel={(option) => SCHOOL_TYPE_LABELS[option]}
              renderInput={(params) => (
                <TextField {...params} label="Types" placeholder="Select types" />
              )}
              renderOption={(props, option, { selected }) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps}>
                    <Checkbox
                      checked={selected}
                      sx={{ mr: 1 }}
                    />
                    {SCHOOL_TYPE_LABELS[option]}
                  </li>
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={SCHOOL_TYPE_LABELS[option]}
                      size="small"
                      {...tagProps}
                    />
                  );
                })
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Autocomplete
              multiple
              size="small"
              options={uniqueTeams}
              value={teamFilter}
              onChange={(e, newValue) => setTeamFilter(newValue)}
              disableCloseOnSelect
              renderInput={(params) => (
                <TextField {...params} label="Teams" placeholder="Select teams" />
              )}
              renderOption={(props, option, { selected }) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps}>
                    <Checkbox
                      checked={selected}
                      sx={{ mr: 1 }}
                    />
                    {option}
                  </li>
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={option}
                      size="small"
                      {...tagProps}
                    />
                  );
                })
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <ToggleButtonGroup
              value={dataStatusFilter}
              exclusive
              onChange={(e, newValue) => {
                if (newValue !== null) {
                  setDataStatusFilter(newValue);
                }
              }}
              size="small"
              fullWidth
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="with-data">With Data</ToggleButton>
              <ToggleButton value="without-data">Without Data</ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              fullWidth
              disabled={sortedSchools.length === 0}
            >
              Export CSV
            </Button>
          </Grid>
        </Grid>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Showing {sortedSchools.length} of {schools.length} schools
        </Typography>
      </Paper>

      {/* Data Table */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} size="small">
          <TableHead>
            <TableRow>
              <TableCell width={50} />
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  School Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'state'}
                  direction={orderBy === 'state' ? order : 'asc'}
                  onClick={() => handleSort('state')}
                >
                  State
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'type'}
                  direction={orderBy === 'type' ? order : 'asc'}
                  onClick={() => handleSort('type')}
                >
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={orderBy === 'has_data'}
                  direction={orderBy === 'has_data' ? order : 'asc'}
                  onClick={() => handleSort('has_data')}
                >
                  Has Data
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'metric_types'}
                  direction={orderBy === 'metric_types' ? order : 'asc'}
                  onClick={() => handleSort('metric_types')}
                >
                  Metric Type
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'metric_categories'}
                  direction={orderBy === 'metric_categories' ? order : 'asc'}
                  onClick={() => handleSort('metric_categories')}
                >
                  Metric Category
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'teams'}
                  direction={orderBy === 'teams' ? order : 'asc'}
                  onClick={() => handleSort('teams')}
                >
                  Teams
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedSchools.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No schools match the current filters
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedSchools.map((school) => {
                const schoolType = detectSchoolType(school);
                const typeInfo = getSchoolTypeInfo(schoolType);
                
                return (
                <React.Fragment key={school.uuid}>
                  <TableRow hover>
                    <TableCell>
                      {school.has_data && school.metrics && school.metrics.length > 0 ? (
                        <IconButton
                          size="small"
                          onClick={() => handleToggleRow(school.uuid)}
                        >
                          {expandedRows.has(school.uuid) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      ) : null}
                    </TableCell>
                    <TableCell>{school.name}</TableCell>
                    <TableCell>{school.state || '-'}</TableCell>
                    <TableCell>
                      <Tooltip title={typeInfo.description}>
                        <Chip 
                          label={typeInfo.label} 
                          size="small" 
                          color={typeInfo.color}
                          variant="outlined"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      {school.has_data ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CancelIcon color="error" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatMetricType(school.metric_types)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatMetricCategory(school.metric_categories)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {school.teams ? (
                          getUniqueTeams(school.teams).map((team, idx) => (
                            <Chip key={idx} label={team} size="small" variant="outlined" />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                  {school.has_data && school.metrics && school.metrics.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0, borderBottom: expandedRows.has(school.uuid) ? undefined : 'none' }}>
                        <Collapse in={expandedRows.has(school.uuid)} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 3, bgcolor: 'grey.50' }}>
                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 'bold' }}>
                              Academic Metrics
                            </Typography>
                            <Grid container spacing={2}>
                              {school.metrics.map((metric, idx) => {
                                const formattedValue = formatMetricValue(metric);
                                return (
                                  <Grid item xs={12} sm={6} md={4} key={idx}>
                                    <Paper sx={{ p: 2, bgcolor: 'white' }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                        {formatMetricCategory(metric.category)}
                                      </Typography>
                                      <Typography variant="h6" sx={{ my: 0.5 }}>
                                        {formattedValue.primary}
                                      </Typography>
                                      {metric.notes && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                                          {metric.notes}
                                        </Typography>
                                      )}
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">
                                          {formatMetricType(metric.type)}
                                        </Typography>
                                        {formattedValue.secondary && (
                                          <Chip 
                                            label={formattedValue.secondary} 
                                            size="small" 
                                            color="primary" 
                                            variant="outlined"
                                          />
                                        )}
                                      </Box>
                                    </Paper>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default AcademicData;

